import { AnalyticsContextValue } from "@swiggy-private/checkout-core";
import { SCREEN_NAME, CLICK_ACTIONS, IMPRESSION_ACTIONS } from "../constants";
import {
    BaseGiftingPayload,
    GiftingCouponEventParams,
    GiftingPopupEventParams,
    GiftingAppliedCouponEventParams,
    GiftingSavingsEventParams,
    GiftingOffersEventParams,
} from "@cart-client/interfaces/Coupon";

// Helper function to build gifting payload
const buildGiftingPayload = (
    basePayload: BaseGiftingPayload,
    isCouponSelected?: boolean,
    isCouponApplied?: boolean,
): string => {
    return JSON.stringify({ ...(basePayload ?? {}), isCouponSelected, isCouponApplied });
};

export const impressionGiftingOffersEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    params: GiftingOffersEventParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.GIFTING_APPLY_COUPON,
        action: IMPRESSION_ACTIONS.IMPRESSION_OFFERS_COUPON_PAGE,
        context: JSON.stringify(params.contextPayload),
        extraParams: buildGiftingPayload(params.basePayload),
    });
};

export const impressionGiftingCouponOffersEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    params: GiftingCouponEventParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.GIFTING_APPLY_COUPON,
        action: IMPRESSION_ACTIONS.IMPRESSION_INDIVIDUAL_OFFERS_COUPON_PAGE,
        context: JSON.stringify(params.contextPayload),
        label: JSON.stringify({ msgtext: params.additionalParams?.description || "" }),
        extraParams: buildGiftingPayload(
            params.basePayload,
            params.additionalParams?.isCouponSelected,
            params.additionalParams?.isCouponApplied,
        ),
    });
};

export const clickGiftingCouponSelectCheckboxEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    params: GiftingCouponEventParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.GIFTING_APPLY_COUPON,
        action: CLICK_ACTIONS.CLICK_COUPON_SELECT_CHECKBOX,
        context: JSON.stringify(params.contextPayload),
        label: JSON.stringify({ msgtext: params.additionalParams?.description || "" }),
        extraParams: buildGiftingPayload(
            params.basePayload,
            params.additionalParams?.isCouponSelected,
            params.additionalParams?.isCouponApplied,
        ),
    });
};

export const clickGiftingCouponDeSelectCheckboxEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    params: GiftingCouponEventParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.GIFTING_APPLY_COUPON,
        action: CLICK_ACTIONS.CLICK_COUPON_DESELECT_CHECKBOX,
        context: JSON.stringify(params.contextPayload),
        label: JSON.stringify({ msgtext: params.additionalParams?.description || "" }),
        extraParams: buildGiftingPayload(
            params.basePayload,
            params.additionalParams?.isCouponSelected,
            params.additionalParams?.isCouponApplied,
        ),
    });
};

export const impressionGiftingApplySelectedEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    params: GiftingSavingsEventParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.GIFTING_APPLY_COUPON,
        action: IMPRESSION_ACTIONS.IMPRESSION_APPLY_SELECTED_COUPON_PAGE,
        context: JSON.stringify(params.contextPayload),
        extraParams: buildGiftingPayload(params.basePayload),
    });
};

export const clickGiftingApplySelectedEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    params: GiftingAppliedCouponEventParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.GIFTING_APPLY_COUPON,
        action: CLICK_ACTIONS.CLICK_APPLY_SELECTED_COUPON_PAGE,
        context: JSON.stringify(params.contextPayload),
        extraParams: buildGiftingPayload(params.basePayload),
    });
};

export const impressionGiftingSearchBarEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    params: {
        placeholder: string;
        basePayload: BaseGiftingPayload;
    },
): void => {
    impressionEvent({
        category: SCREEN_NAME.GIFTING_APPLY_COUPON,
        action: IMPRESSION_ACTIONS.IMPRESSION_SEARCH_BAR_COUPON_PAGE,
        label: JSON.stringify({ msgtext: params.placeholder }),
        extraParams: buildGiftingPayload(params.basePayload),
    });
};

export const clickGiftingBackEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    params: {
        basePayload?: BaseGiftingPayload;
    },
): void => {
    clickEvent({
        category: SCREEN_NAME.GIFTING_APPLY_COUPON,
        action: CLICK_ACTIONS.CLICK_BACK_COUPON_PAGE,
        extraParams: buildGiftingPayload(params.basePayload || {}),
    });
};

export const clickGiftingApplyEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    params: {
        couponsearchtext: string;
        basePayload: BaseGiftingPayload;
    },
): void => {
    clickEvent({
        category: SCREEN_NAME.GIFTING_APPLY_COUPON,
        action: CLICK_ACTIONS.CLICK_APPLY_SEARCH_BAR,
        context: JSON.stringify({ couponsearchtext: params.couponsearchtext }),
        extraParams: buildGiftingPayload(params.basePayload),
    });
};

export const impressionGiftingPopUpEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    params: GiftingPopupEventParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.GIFTING_CHECKOUT,
        action: IMPRESSION_ACTIONS.IMPRESSION_POP_UP,
        context: JSON.stringify(params.contextPayload),
        extraParams: buildGiftingPayload(params.basePayload),
    });
};

export const clickGiftingPopUpEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    params: GiftingPopupEventParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.GIFTING_CHECKOUT,
        action: CLICK_ACTIONS.CLICK_POP_UP,
        context: JSON.stringify(params.contextPayload),
        extraParams: buildGiftingPayload(params.basePayload),
    });
};

export const clickGiftingCrossPopUpEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    params: GiftingPopupEventParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.GIFTING_CHECKOUT,
        action: CLICK_ACTIONS.CLICK_CROSS_POP_UP,
        context: JSON.stringify(params.contextPayload),
        extraParams: buildGiftingPayload(params.basePayload),
    });
};
